<template>
  <div class="rv-settings">
    <div v-if="loading && !form" class="rv-loading">
      <div class="spinner-border text-primary" role="status"><span class="sr-only">Loading...</span></div>
    </div>

    <div v-else-if="form" class="rv-settings-grid">
      <!-- ================= LEFT COLUMN ================= -->
      <div class="rv-settings-col">

        <!-- Master toggle -->
        <div class="rv-card rv-master" :class="{ 'is-on': form.enabled }">
          <div class="rv-master-icon"><i class="fa fa-paper-plane"></i></div>
          <div class="rv-master-text">
            <div class="rv-card-title">Automatic review requests</div>
            <div class="rv-card-sub">{{ form.enabled ? 'On — requests fire automatically after completed visits.' : 'Off — no review requests are sent.' }}</div>
          </div>
          <label class="rv-switch">
            <input type="checkbox" v-model="form.enabled" />
            <span class="rv-switch-track"><span class="rv-switch-thumb"></span></span>
          </label>
        </div>

        <!-- The one combination that fails silently: sending is on, but the happy branch of
             the rating page has no link to offer. Only patients would ever see that. -->
        <div v-if="reviewLinkMissing" class="rv-link-warning">
          <i class="fa fa-exclamation-triangle"></i>
          <div>
            <b>Add a review link before you turn this on.</b>
            Patients who rate you {{ form.gateThreshold }}+ stars are asked to post that review
            publicly — without a link there is nothing for them to tap. Paste your Google review
            link under <b>Review links</b> below.
          </div>
        </div>

        <!-- Dimmed sub-section -->
        <div class="rv-subsection" :class="{ 'is-disabled': !form.enabled }">

          <!-- Message templates -->
          <div class="rv-card">
            <div class="rv-card-title">Message templates</div>
            <div class="rv-card-sub mb-3">Choose what the first request says. Send by text, email, or both.</div>

            <div class="rv-channel-toggles">
              <label class="rv-channel-toggle">
                <span class="rv-switch">
                  <input type="checkbox" v-model="form.channels.sms" />
                  <span class="rv-switch-track"><span class="rv-switch-thumb"></span></span>
                </span>
                <span class="rv-channel-label">Text (SMS)</span>
              </label>
              <label class="rv-channel-toggle">
                <span class="rv-switch">
                  <input type="checkbox" v-model="form.channels.email" />
                  <span class="rv-switch-track"><span class="rv-switch-thumb"></span></span>
                </span>
                <span class="rv-channel-label">Email</span>
              </label>
            </div>

            <div class="rv-template-grid">
              <div>
                <label class="rv-field-label">SMS template</label>
                <multiselect
                  v-model="smsTemplateObj"
                  :options="smsOptions"
                  :multiple="false"
                  :show-labels="false"
                  :allow-empty="true"
                  label="Name"
                  track-by="WikiPageID"
                  placeholder="Select an SMS template"
                />
                <p class="rv-hint">Shows Wiki pages tagged <b>review-request</b> + <b>sms-template</b>.</p>
                <SmsGuidance :text="smsPreviewText" substitute-tags :show-balance="false" />
              </div>
              <div>
                <label class="rv-field-label">Email template</label>
                <multiselect
                  v-model="emailTemplateObj"
                  :options="emailOptions"
                  :multiple="false"
                  :show-labels="false"
                  :allow-empty="true"
                  label="Name"
                  track-by="WikiPageID"
                  placeholder="Select an email template"
                />
                <p class="rv-hint">Shows Wiki pages tagged <b>review-request</b> + <b>email-template</b>.</p>
              </div>
            </div>
          </div>

          <!-- Timing -->
          <div class="rv-card">
            <div class="rv-card-title">Timing</div>
            <div class="rv-card-sub mb-3">When the first request goes out after a visit is marked complete in Open Dental.</div>

            <div class="rv-radio-row">
              <input type="radio" id="timing-delay" value="delay" v-model="form.timing.mode" />
              <label for="timing-delay" class="rv-radio-inline">
                <span>Send</span>
                <input
                  type="number" min="1"
                  class="rv-num-input"
                  v-model.number="form.timing.delayValue"
                  :disabled="form.timing.mode !== 'delay'"
                />
                <select class="rv-select rv-unit" v-model="form.timing.delayUnit" :disabled="form.timing.mode !== 'delay'">
                  <option value="minutes">minutes</option>
                  <option value="hours">hours</option>
                  <option value="days">days</option>
                </select>
                <span>after the appointment</span>
              </label>
            </div>

            <div class="rv-radio-row mt-2">
              <input type="radio" id="timing-time" value="timeOfDay" v-model="form.timing.mode" />
              <div class="rv-radio-stack">
                <label for="timing-time" class="rv-radio-inline">
                  <span>Send at</span>
                  <select class="rv-select rv-time" v-model="form.timing.sendTime" :disabled="form.timing.mode !== 'timeOfDay'">
                    <option v-for="opt in sendTimeOptions" :key="opt.value" :value="opt.value">{{ opt.text }}</option>
                  </select>
                  <span>on the day of the visit</span>
                </label>
                <span class="rv-radio-desc">Visits marked complete after this time get their request at the same time the next day.</span>
              </div>
            </div>
          </div>

          <!-- Follow-up -->
          <div class="rv-card">
            <div class="rv-card-head-row">
              <div>
                <div class="rv-card-title">Follow-up reminder</div>
                <div class="rv-card-sub">A gentle nudge for patients who didn't respond. Never sent to anyone who already left a review.</div>
              </div>
              <label class="rv-switch">
                <input type="checkbox" v-model="form.followUps.enabled" />
                <span class="rv-switch-track"><span class="rv-switch-thumb"></span></span>
              </label>
            </div>
            <div v-if="form.followUps.enabled" class="rv-followup-config">
              <span>Remind after</span>
              <input type="number" min="1" class="rv-num-input" v-model.number="form.followUps.delayValue" />
              <select class="rv-select rv-unit" v-model="form.followUps.delayUnit">
                <option value="minutes">minutes</option>
                <option value="hours">hours</option>
                <option value="days">days</option>
              </select>
              <span>— up to</span>
              <select class="rv-select" v-model.number="form.followUps.maxCount">
                <option :value="1">1 reminder</option>
                <option :value="2">2 reminders</option>
                <option :value="3">3 reminders</option>
              </select>
            </div>
          </div>

          <!-- Procedure code exclusions -->
          <div class="rv-card">
            <div class="rv-card-title">Procedure code exclusions</div>
            <div class="rv-card-sub mb-3">Don't send a review request for appointments containing one or more of these procedure codes.</div>
            <!--
              Same picker as Insurance Verification → Precheck settings → Procedure codes.
              Deliberately NOT :taggable (IV's is): the worker matches procedurelog →
              procedurecode by CodeNum, so a code missing from this office's catalog could
              never match an appointment — typing one in would save an exclusion that does nothing.
            -->
            <multiselect
              class="rv-proc-select"
              :value="selectedExclusionOptions"
              :options="exclusionOptions"
              :multiple="true"
              :searchable="true"
              :close-on-select="false"
              :show-labels="false"
              :loading="procCodesLoading"
              label="text"
              track-by="value"
              placeholder="Search procedure codes (e.g. D1110)…"
              @input="onExclusionChange"
            >
              <!-- Chips stay code-only; the description rides along as the hover title. -->
              <template slot="tag" slot-scope="{ option }">
                <span class="rv-chip" :title="option.desc || ''">
                  {{ option.value }}
                  <button
                    type="button"
                    class="rv-chip-x"
                    :aria-label="'Remove ' + option.value"
                    @click.stop="removeExclusion(option.value)"
                  ><i class="fa fa-times" aria-hidden="true"></i></button>
                </span>
              </template>
              <template slot="noResult">No procedure codes match.</template>
            </multiselect>

            <div class="rv-proc-foot">
              <div class="rv-hint">{{ form.excludedProcCodes.length }} excluded · appointments with any of these are skipped.</div>
              <button v-if="form.excludedProcCodes.length" type="button" class="rv-linkbtn" @click="clearExclusions">Clear all</button>
            </div>
            <div v-if="procCodesLoadFailed" class="rv-hint rv-hint-warn">Couldn't load your procedure codes — try reloading.</div>
          </div>

          <!-- Who gets asked (sendRules). Automation-only safety rules: a manual send from the
               Patient Profile shows a warning instead of being blocked. -->
          <div class="rv-card" data-test="reviews-send-rules">
            <div class="rv-card-title">Who gets asked</div>
            <div class="rv-card-sub mb-3">Rules for automatic requests. Sending by hand from a patient's profile still works — it shows a warning instead.</div>

            <div class="rv-rule-row">
              <label for="rv-frequency-cap" class="rv-rule-label">Don't auto-ask the same patient again within</label>
              <select
                id="rv-frequency-cap"
                class="rv-select rv-cap"
                data-test="reviews-frequency-cap"
                v-model="form.sendRules.frequencyCapDays"
              >
                <option v-for="opt in frequencyCapOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
              </select>
            </div>

            <div class="rv-card-head-row rv-rule-toggle">
              <div>
                <div class="rv-rule-title">Never auto-ask patients who rated 1–3 stars or have open private feedback</div>
                <div class="rv-card-sub">Uses the patient's last rating, whatever your public review gate is set to.</div>
              </div>
              <label class="rv-switch">
                <input type="checkbox" data-test="reviews-suppress-low-rating" v-model="form.sendRules.suppressAfterLowRating" />
                <span class="rv-switch-track"><span class="rv-switch-thumb"></span></span>
              </label>
            </div>
          </div>

          <!-- Public review gate -->
          <div class="rv-card">
            <div class="rv-card-title">Public review gate</div>
            <div class="rv-card-sub mb-3">Happy patients are guided to Google. Anyone below the bar is routed to your private feedback inbox — never a public page.</div>
            <div class="rv-gate-options">
              <label class="rv-gate-option" :class="{ active: form.gateThreshold === 4 }">
                <input type="radio" :value="4" v-model.number="form.gateThreshold" />
                <div>
                  <div class="rv-gate-title">4 stars and up go to Google</div>
                  <div class="rv-gate-desc">Recommended. 3 stars or fewer stay private.</div>
                </div>
              </label>
              <label class="rv-gate-option" :class="{ active: form.gateThreshold === 5 }">
                <input type="radio" :value="5" v-model.number="form.gateThreshold" />
                <div>
                  <div class="rv-gate-title">5 stars only go to Google</div>
                  <div class="rv-gate-desc">Strictest. 4 stars or fewer stay private.</div>
                </div>
              </label>
            </div>

            <div class="rv-notify">
              <label class="rv-field-label">Email private feedback alerts to</label>
              <input
                type="text"
                class="rv-text-input"
                v-model="notifyEmailsText"
                placeholder="frontdesk@practice.com, manager@practice.com"
              />
              <p class="rv-hint">Comma-separated. These addresses get an email the moment a private note comes in.</p>
            </div>
          </div>

          <!-- Review links -->
          <div class="rv-card">
            <div class="rv-card-title">Review links</div>
            <div class="rv-card-sub">Which platforms happy patients are offered. Teamio can't post reviews for you — paste each profile's public review URL.</div>
            <div class="rv-link-list">
              <div v-for="lk in linkRows" :key="lk.platform" class="rv-link-row">
                <div class="rv-link-mark" :style="{ background: lk.brandBg, color: lk.brandColor }">{{ lk.mark }}</div>
                <div class="rv-link-body">
                  <div class="rv-link-name-row">
                    <span class="rv-link-name">{{ lk.name }}</span>
                    <span v-if="form.reviewLinks[lk.platform].verifiedAt" class="rv-verified"><i class="fa fa-check-circle"></i>Verified</span>
                    <span v-else-if="form.reviewLinks[lk.platform].url" class="rv-unverified"><i class="fa fa-exclamation-circle"></i>Needs verification</span>
                  </div>

                  <!-- Google Business Profile connect (Phase B1) — OAuth status block
                       sits above the manual URL, which stays as an override. -->
                  <div v-if="lk.platform === 'google'" class="rv-gc">
                    <!-- Connected -->
                    <div v-if="googleConnected" class="rv-gc-connected">
                      <span class="rv-gc-status"><i class="fa fa-check-circle"></i>Connected as <b>{{ googleLocationLabel }}</b></span>
                      <button type="button" class="rv-btn rv-btn-secondary rv-gc-btn" :disabled="googleLoading" @click="disconnectGoogle">Disconnect</button>
                    </div>

                    <!-- Needs a location choice (multi-location account) -->
                    <div v-else-if="googleNeedsLocation" class="rv-gc-needs">
                      <div class="rv-gc-needs-label">Choose which Google location to connect:</div>
                      <div class="rv-gc-needs-row">
                        <b-form-select v-model="selectedGoogleLocation" :options="googleLocationOptions" class="rv-gc-select" />
                        <button type="button" class="rv-btn rv-btn-primary rv-gc-btn" :disabled="googleLoading || !selectedGoogleLocation" @click="useSelectedGoogleLocation">Use this location</button>
                      </div>
                    </div>

                    <!-- Pending Google's API approval — the connect flow cannot
                         succeed yet, so don't offer a button that only errors. -->
                    <div v-else-if="!googleConnectAvailable" class="rv-gc-pending">
                      <i class="fa fa-clock-o"></i>
                      <span>
                        <b>Google connection coming soon.</b>
                        We're waiting on Google to approve Teamio's Business Profile API access.
                        In the meantime, paste your Google review link below — it works exactly the same for your patients.
                      </span>
                    </div>

                    <!-- Not connected / errored -->
                    <div v-else class="rv-gc-disconnected">
                      <button type="button" class="rv-btn rv-btn-primary rv-gc-btn" :disabled="googleLoading" @click="connectGoogle">
                        <i class="fa fa-google"></i> Connect Google Business Profile
                      </button>
                      <span v-if="googleError" class="rv-gc-error"><i class="fa fa-exclamation-circle"></i>{{ googleError }}</span>
                    </div>
                  </div>

                  <div class="rv-link-input-row">
                    <input
                      type="text"
                      class="rv-text-input rv-mono"
                      :value="form.reviewLinks[lk.platform].url"
                      @input="onLinkUrl(lk.platform, $event.target.value)"
                      :placeholder="lk.placeholder"
                    />
                    <button class="rv-btn rv-btn-secondary" @click="verifyLink(lk.platform)">
                      {{ form.reviewLinks[lk.platform].verifiedAt ? 'Re-verify' : 'Verify' }}
                    </button>
                  </div>
                </div>
                <label class="rv-switch rv-link-switch">
                  <input type="checkbox" v-model="form.reviewLinks[lk.platform].enabled" />
                  <span class="rv-switch-track"><span class="rv-switch-thumb"></span></span>
                </label>
              </div>
            </div>
          </div>

        </div><!-- /rv-subsection -->

        <div class="rv-save-bar">
          <button class="rv-btn rv-btn-primary" @click="save" :disabled="saving">
            <i class="fa fa-check"></i> {{ saving ? 'Saving…' : 'Save settings' }}
          </button>
        </div>
      </div>

      <!-- ================= PREVIEW RAIL ================= -->
      <div class="rv-preview-rail">
        <div class="rv-card rv-preview-card">
          <div class="rv-preview-head"><i class="fa fa-mobile"></i>Live preview</div>

          <div v-if="form.channels.sms" class="rv-preview-block">
            <div class="rv-preview-label">TEXT MESSAGE</div>
            <div class="rv-sms-bubble">{{ smsPreviewText }}</div>
            <div class="rv-preview-note">Direct link · 1 tap to review</div>
          </div>

          <div v-if="form.channels.email" class="rv-preview-block">
            <div class="rv-preview-label">EMAIL</div>
            <div class="rv-email-card">
              <div class="rv-email-header">
                <div class="rv-email-logo">{{ practiceInitial }}</div>
                <span>{{ practiceName }}</span>
              </div>
              <div class="rv-email-body">
                <div class="rv-email-title">How was your visit?</div>
                <div class="rv-email-text">{{ emailPreviewText }}</div>
                <div class="rv-email-cta">Leave a review</div>
              </div>
            </div>
          </div>

          <div v-if="!form.channels.sms && !form.channels.email" class="rv-preview-empty">
            Turn on Text or Email above to see the message preview.
          </div>
        </div>

        <div class="rv-preview-tip">
          <i class="fa fa-info-circle"></i>
          <span>Every request is tracked per patient, so no one is asked twice and follow-ups only reach people who haven't responded.</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { mapState, mapActions } from 'vuex';
import mixin from '../../../mixin.js';
import Multiselect from 'vue-multiselect';
// Imported explicitly rather than relied on: without it this page only looks right because Routes.js bundles every page and someone else's import lands first (same note as PrecheckSettingsModal.vue).
import 'vue-multiselect/dist/vue-multiselect.min.css';
import SmsGuidance from '@/components/SmsGuidance';
import { GOOGLE_CONNECT_RELEASED } from '../googleConnect';
import { SEND_TIME_OPTIONS, isValidSendTime, timingForForm } from '../reviewsTiming';
import { buildExclusionOptions, exclusionOptionsForSelection } from '../procCodeOptions';
import { FREQUENCY_CAP_OPTIONS, sendRulesForForm, validateSendRules } from '../doNotSend';

// Merge-tag sample values so the SMS credit estimate + preview match a real send.
const SAMPLE_VARS = {
  first_name: 'Alex',
  name: 'Alex Rivera',
  practice_name: 'your dental office',
  review_link: 'teamio.app/rv/ab12cd34',
};

const LINK_META = {
  google: { name: 'Google', mark: 'G', brandBg: '#e8f0fe', brandColor: '#4285F4', placeholder: 'https://g.page/r/your-practice/review', domains: ['google.', 'g.page', 'goo.gl', 'maps.app.goo.gl'] },
  facebook: { name: 'Facebook', mark: 'f', brandBg: '#e7f0ff', brandColor: '#1877F2', placeholder: 'https://facebook.com/your-practice/reviews', domains: ['facebook.com', 'fb.com', 'fb.me'] },
  yelp: { name: 'Yelp', mark: 'Y', brandBg: '#fdecec', brandColor: '#d32323', placeholder: 'https://yelp.com/biz/your-practice', domains: ['yelp.', 'yelp.to'] },
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Shared release flag — see googleConnect.js for why it is NOT declared here.

export default {
  name: 'ReviewsSettings',
  components: { Multiselect, SmsGuidance },
  mixins: [mixin],

  data() {
    return {
      form: null,
      wikiTemplates: [],
      smsTemplateObj: null,
      emailTemplateObj: null,
      notifyEmailsText: '',
      procCodesLoadFailed: false,
      selectedGoogleLocation: null,
    };
  },

  async created() {
    await this.init();
  },

  methods: {
    ...mapActions('reviews', [
      'getReviewsSettings', 'saveReviewsSettings', 'getReviewProcCodes',
      'getGoogleConnection', 'startGoogleConnect', 'selectGoogleLocation', 'disconnectGoogleReviews',
    ]),
    ...mapActions('wiki', ['getWikiPages']),

    async init() {
      // Handle the OAuth callback params (?google=…) first — toast + strip the
      // params so a refresh doesn't re-toast (mirrors Settings.loadSlackStatus).
      this.handleGoogleReturn();
      // The procedure-code catalog loads on its own (not awaited, not in the Promise.all):
      // it used to sit inside it, so a failed catalog query blanked the WHOLE settings form.
      this.loadProcCodes();
      try {
        const [settingsResp] = await Promise.all([
          this.getReviewsSettings({ siteID: this.siteID }),
          this.loadWikiTemplates(),
        ]);
        this.hydrate(settingsResp && settingsResp.settings);
      } catch (err) {
        console.error('[ReviewsSettings] init failed:', err);
        this.$toast && this.$toast.error('Could not load review settings.');
      }
      // Hydrate the Google connection independently of settings — the endpoint
      // may be unavailable while the backend ships, and a failure here must not
      // block the settings form from rendering.
      this.loadGoogleConnection();
    },

    // Read ?google=<status> off the callback landing URL, toast, then strip the
    // google/location/message params (Slack idiom — window.history.replaceState).
    handleGoogleReturn() {
      const params = new URLSearchParams(window.location.search);
      const status = params.get('google');
      if (!status) return;
      const location = params.get('location');
      const message = params.get('message');

      if (status === 'connected') {
        this.$toast && this.$toast.success(`Connected to Google${location ? ': ' + location : ''}`);
      } else if (status === 'select_location') {
        this.$toast && this.$toast.info('Choose which location to connect');
      } else if (status === 'error') {
        this.$toast && this.$toast.error(`Google connection failed${message ? ': ' + message : ''}`);
      } else if (status === 'cancelled') {
        this.$toast && this.$toast('Google connection cancelled');
      }

      const url = new URL(window.location);
      url.searchParams.delete('google');
      url.searchParams.delete('location');
      url.searchParams.delete('message');
      window.history.replaceState({}, document.title, url.pathname + url.search + url.hash);
    },

    async loadProcCodes() {
      this.procCodesLoadFailed = false;
      try {
        await this.getReviewProcCodes({ siteID: this.siteID });
      } catch (err) {
        console.error('[ReviewsSettings] procedure code load failed:', err);
        this.procCodesLoadFailed = true;
      }
    },

    async loadGoogleConnection() {
      try {
        await this.getGoogleConnection({ siteID: this.siteID });
        // Pre-select the first location when the account needs a choice.
        const conn = this.googleConnection;
        if (conn && conn.status === 'needs_location' && !this.selectedGoogleLocation
            && Array.isArray(conn.availableLocations) && conn.availableLocations.length) {
          this.selectedGoogleLocation = conn.availableLocations[0].name;
        }
      } catch (err) {
        console.error('[ReviewsSettings] Google connection load failed:', err);
      }
    },

    // Full-page redirect to Google consent (not a popup) — Slack connectSlack idiom.
    async connectGoogle() {
      try {
        const { authUrl } = await this.startGoogleConnect({ siteID: this.siteID, returnUrl: window.location.origin });
        if (authUrl) {
          window.location.href = authUrl;
        } else {
          this.$toast && this.$toast.error('Could not start the Google connection.');
        }
      } catch (err) {
        console.error('[ReviewsSettings] Google connect failed:', err);
        this.$toast && this.$toast.error('Could not start the Google connection.');
      }
    },

    async useSelectedGoogleLocation() {
      if (!this.selectedGoogleLocation) return;
      try {
        await this.selectGoogleLocation({ siteID: this.siteID, locationName: this.selectedGoogleLocation });
        await this.getGoogleConnection({ siteID: this.siteID });
        this.$toast && this.$toast.success('Google location connected.');
      } catch (err) {
        console.error('[ReviewsSettings] Google location select failed:', err);
        this.$toast && this.$toast.error('Could not connect that location.');
      }
    },

    async disconnectGoogle() {
      if (!confirm('Disconnect your Google Business Profile? Google review monitoring will stop until you reconnect.')) return;
      try {
        await this.disconnectGoogleReviews({ siteID: this.siteID });
        this.selectedGoogleLocation = null;
        this.$toast && this.$toast.success('Google disconnected.');
      } catch (err) {
        console.error('[ReviewsSettings] Google disconnect failed:', err);
        this.$toast && this.$toast.error('Could not disconnect Google.');
      }
    },

    async loadWikiTemplates() {
      try {
        const res = await this.getWikiPages({ siteID: this.siteID, tags: 'review-request' });
        this.wikiTemplates = (res && res[0]) || [];
      } catch (err) {
        console.error('[ReviewsSettings] wiki template load failed:', err);
        this.wikiTemplates = [];
      }
    },

    hydrate(settings) {
      // The backend always returns a full defaults-merged blob — deep-clone it so
      // edits never mutate the Vuex copy.
      const s = JSON.parse(JSON.stringify(settings || {}));
      // Normalizes an old 'eveningWindow' blob (no radio for it any more) and fills sendTime.
      s.timing = timingForForm(s.timing);
      if (!Array.isArray(s.excludedProcCodes)) s.excludedProcCodes = [];
      // An older API response (before the sendRules deploy) has no sendRules — fill the
      // server defaults (6 months, on) so the card renders and a Save writes them explicitly.
      s.sendRules = sendRulesForForm(s.sendRules);
      this.form = s;

      // Resolve the picked template objects from the LIVE wiki list by WikiPageID
      // (fallback to the stored snapshot if the page is missing from the list).
      this.smsTemplateObj = this.resolveTemplate(s.smsTemplateID, s.smsTemplateSnapshot);
      this.emailTemplateObj = this.resolveTemplate(s.emailTemplateID, s.emailTemplateSnapshot);

      const emails = (s.privateFeedback && s.privateFeedback.notifyEmails) || [];
      this.notifyEmailsText = emails.join(', ');
    },

    resolveTemplate(wikiPageID, snapshot) {
      if (!wikiPageID) return null;
      const live = this.wikiTemplates.find((t) => t.WikiPageID === wikiPageID);
      if (live) return { ...live };
      return snapshot ? { ...snapshot } : null;
    },

    // Re-sync each template slot from the live wiki list on save (self-healing —
    // prevents the stale-snapshot class of bug; see AptReminderSettings._refreshTemplateSlots).
    _refreshTemplateSlots() {
      [['smsTemplateObj'], ['emailTemplateObj']].forEach(([key]) => {
        const slot = this[key];
        if (!slot || !slot.WikiPageID) return;
        const fresh = this.wikiTemplates.find((t) => t.WikiPageID === slot.WikiPageID);
        if (fresh) this[key] = { ...fresh };
      });
    },

    onLinkUrl(platform, value) {
      // Editing the URL invalidates any prior verification.
      this.form.reviewLinks[platform].url = value;
      this.form.reviewLinks[platform].verifiedAt = null;
    },

    verifyLink(platform) {
      const url = (this.form.reviewLinks[platform].url || '').trim();
      if (!url) {
        this.$toast && this.$toast.error('Paste a review URL first.');
        return;
      }
      if (!/^https:\/\//i.test(url)) {
        this.$toast && this.$toast.error('Review links must start with https://');
        return;
      }
      let host = '';
      try {
        host = new URL(url).hostname.toLowerCase();
      } catch (e) {
        this.$toast && this.$toast.error('That doesn\'t look like a valid URL.');
        return;
      }
      const meta = LINK_META[platform];
      const ok = meta.domains.some((d) => host.includes(d));
      if (!ok) {
        this.$toast && this.$toast.error(`That URL doesn't look like a ${meta.name} link.`);
        return;
      }
      this.form.reviewLinks[platform].verifiedAt = new Date().toISOString();
      this.$toast && this.$toast.success(`${meta.name} link verified.`);
    },

    // The picker hands back option objects; only the procCode strings are stored.
    onExclusionChange(selected) {
      this.form.excludedProcCodes = (Array.isArray(selected) ? selected : []).map((o) => o.value);
    },

    removeExclusion(code) {
      this.form.excludedProcCodes = this.form.excludedProcCodes.filter((c) => c !== code);
    },

    clearExclusions() {
      this.form.excludedProcCodes = [];
    },

    _extractPlain(content) {
      if (!content) return '';
      try {
        const parsed = JSON.parse(content);
        if (parsed && Array.isArray(parsed.blocks)) {
          return parsed.blocks
            .map((b) => (b && b.data && (b.data.text || b.data.caption)) || '')
            .map((t) => String(t).replace(/<[^>]+>/g, ''))
            .filter(Boolean)
            .join(' ')
            .trim();
        }
      } catch (e) { /* not Editor.js JSON — fall through */ }
      return String(content).replace(/<[^>]+>/g, '').trim();
    },

    _applySampleVars(text) {
      let out = text || '';
      Object.keys(SAMPLE_VARS).forEach((k) => {
        out = out.replace(new RegExp(`{{\\s*${k}\\s*}}`, 'gi'), SAMPLE_VARS[k]);
      });
      return out;
    },

    // Platforms the rating page can actually send a happy patient to — the same test the
    // server runs (enabledReviewLinks in reviews-patient.js drops any link that is off or
    // has an empty URL). Verification is advisory and deliberately not required here.
    hasUsableReviewLink(blob) {
      const rl = (blob && blob.reviewLinks) || {};
      return ['google', 'facebook', 'yelp'].some(
        (p) => rl[p] && rl[p].enabled && (rl[p].url || '').trim() !== ''
      );
    },

    validate(blob) {
      const errs = [];
      if (blob.enabled) {
        if (!blob.channels.sms && !blob.channels.email) {
          errs.push('Turn on at least one channel (Text or Email).');
        }
        if (blob.channels.sms && !blob.smsTemplateID) errs.push('Pick an SMS template, or turn off Text.');
        if (blob.channels.email && !blob.emailTemplateID) errs.push('Pick an email template, or turn off Email.');
        // Mirrors the server's launch gate (reviews-service lib/settings.js). Only raised once a
        // channel can actually send, so an office mid-setup isn't nagged about it — otherwise the
        // errors above already tell them what's missing.
        const canSend =
          (blob.channels.sms && blob.smsTemplateID) || (blob.channels.email && blob.emailTemplateID);
        if (canSend && !this.hasUsableReviewLink(blob)) {
          errs.push(
            'Add a review link before turning requests on — without one, patients who rate you 4+ ' +
              'stars have nowhere to leave the review.'
          );
        }
      }
      if (![4, 5].includes(blob.gateThreshold)) errs.push('Choose a public review gate.');
      if (blob.timing.mode === 'timeOfDay' && !isValidSendTime(blob.timing.sendTime)) {
        errs.push('Pick a send time.');
      }
      if (blob.timing.mode === 'delay' && !(Number.isInteger(blob.timing.delayValue) && blob.timing.delayValue > 0)) {
        errs.push('Send delay must be a positive whole number.');
      }
      if (blob.followUps.enabled) {
        if (!(Number.isInteger(blob.followUps.delayValue) && blob.followUps.delayValue > 0)) {
          errs.push('Follow-up delay must be a positive whole number.');
        }
        if (![1, 2, 3].includes(blob.followUps.maxCount)) errs.push('Follow-up count must be 1, 2, or 3.');
      }
      ['google', 'facebook', 'yelp'].forEach((p) => {
        const u = (blob.reviewLinks[p].url || '').trim();
        if (u && !/^https:\/\//i.test(u)) errs.push(`The ${p} URL must start with https://`);
      });
      blob.privateFeedback.notifyEmails.forEach((em) => {
        if (!EMAIL_RE.test(em)) errs.push(`Invalid alert email: ${em}`);
      });
      // Mirrors reviews-service lib/settings.js (cap ∈ {0,30,60,90,180,365}, boolean toggle).
      const ruleErr = validateSendRules(blob.sendRules);
      if (ruleErr) errs.push(ruleErr);
      return errs;
    },

    buildBlob() {
      this._refreshTemplateSlots();
      const f = this.form;

      const notifyEmails = this.notifyEmailsText
        .split(',')
        .map((e) => e.trim())
        .filter(Boolean);

      return {
        enabled: !!f.enabled,
        channels: { sms: !!f.channels.sms, email: !!f.channels.email },
        smsTemplateID: this.smsTemplateObj ? this.smsTemplateObj.WikiPageID : null,
        emailTemplateID: this.emailTemplateObj ? this.emailTemplateObj.WikiPageID : null,
        smsTemplateSnapshot: this.smsTemplateObj ? { ...this.smsTemplateObj } : null,
        emailTemplateSnapshot: this.emailTemplateObj ? { ...this.emailTemplateObj } : null,
        timing: {
          mode: f.timing.mode,
          sendTime: f.timing.sendTime,
          delayValue: f.timing.delayValue,
          delayUnit: f.timing.delayUnit,
        },
        followUps: {
          enabled: !!f.followUps.enabled,
          maxCount: f.followUps.maxCount,
          delayValue: f.followUps.delayValue,
          delayUnit: f.followUps.delayUnit,
        },
        excludedProcCodes: [...f.excludedProcCodes],
        // Who gets asked. Always sent explicitly: the server keeps omitted keys, but only this
        // blob can change them.
        sendRules: {
          frequencyCapDays: f.sendRules.frequencyCapDays,
          suppressAfterLowRating: !!f.sendRules.suppressAfterLowRating,
        },
        gateThreshold: f.gateThreshold,
        reviewLinks: {
          google: { ...f.reviewLinks.google },
          facebook: { ...f.reviewLinks.facebook },
          yelp: { ...f.reviewLinks.yelp },
        },
        privateFeedback: {
          notifyEmails,
          reasons: (f.privateFeedback && f.privateFeedback.reasons) || [],
        },
      };
    },

    async save() {
      const blob = this.buildBlob();
      const errs = this.validate(blob);
      if (errs.length) {
        this.$toast && this.$toast.error(errs[0]);
        return;
      }
      try {
        const data = await this.saveReviewsSettings({ siteID: this.siteID, settings: blob });
        // Adopt the server-echoed (merged + re-verified) settings.
        if (data && data.settings) this.hydrate(data.settings);
        this.$toast && this.$toast.success('Review settings saved.');
      } catch (err) {
        console.error('[ReviewsSettings] save failed:', err);
        const msg = (err && err.response && err.response.data) || (err && err.message) || 'Save failed.';
        this.$toast && this.$toast.error(typeof msg === 'string' ? msg : 'Could not save review settings.');
      }
    },
  },

  computed: {
    ...mapState('reviews', {
      paidFor: (s) => s.paidFor,
      procCodes: (s) => s.procCodes,
      procCodesLoading: (s) => s.loading.procCodes,
      loading: (s) => s.loading.settings,
      saving: (s) => s.loading.saving,
      googleConnection: (s) => s.googleConnection,
      googleLoading: (s) => s.loading.googleConnection,
    }),

    // Requests are on and a channel can send, but the rating page's 4+ star branch has no
    // link to offer — so a happy patient would read "share this on Google?" above no button.
    // Surfaced here, beside the toggle that causes it, rather than only as a Save error.
    reviewLinkMissing() {
      const f = this.form;
      if (!f || !f.enabled) return false;
      const canSend =
        (f.channels.sms && this.smsTemplateObj) || (f.channels.email && this.emailTemplateObj);
      return !!canSend && !this.hasUsableReviewLink(f);
    },

    // ── Google connection view-state (derived from googleConnection) ──
    // Whether the Connect button can actually succeed. See GOOGLE_CONNECT_RELEASED.
    googleConnectAvailable() {
      return GOOGLE_CONNECT_RELEASED;
    },
    googleConnected() {
      const c = this.googleConnection;
      return !!(c && (c.connected || c.status === 'connected'));
    },
    googleNeedsLocation() {
      const c = this.googleConnection;
      return !!(c && c.status === 'needs_location');
    },
    googleError() {
      const c = this.googleConnection;
      return c && c.status === 'error' ? (c.lastError || 'Connection error') : '';
    },
    googleLocationLabel() {
      const c = this.googleConnection;
      if (!c) return 'Google';
      return c.locationTitle || c.locationName || c.accountName || 'Google';
    },
    googleLocationOptions() {
      const c = this.googleConnection;
      const locs = (c && c.availableLocations) || [];
      return locs.map((l) => ({ value: l.name, text: l.title || l.name }));
    },

    smsOptions() {
      return this.wikiTemplates.filter((t) =>
        t.Tags && t.Tags.includes('sms-template') && t.IsSmsTemplate
      );
    },
    emailOptions() {
      return this.wikiTemplates.filter((t) =>
        t.Tags && t.Tags.includes('email-template') && t.IsEmailTemplate
      );
    },

    linkRows() {
      return ['google', 'facebook', 'yelp'].map((platform) => ({ platform, ...LINK_META[platform] }));
    },

    sendTimeOptions() {
      return SEND_TIME_OPTIONS;
    },

    frequencyCapOptions() {
      return FREQUENCY_CAP_OPTIONS;
    },

    exclusionOptions() {
      return buildExclusionOptions(this.procCodes, this.form && this.form.excludedProcCodes);
    },

    selectedExclusionOptions() {
      return exclusionOptionsForSelection(this.exclusionOptions, this.form && this.form.excludedProcCodes);
    },

    smsPreviewText() {
      const raw = this.smsTemplateObj ? this._extractPlain(this.smsTemplateObj.Content) : '';
      const body = raw || `Hi {{first_name}}, thanks for visiting {{practice_name}}! Mind sharing how we did? {{review_link}}`;
      return this._applySampleVars(body);
    },
    emailPreviewText() {
      const raw = this.emailTemplateObj ? this._extractPlain(this.emailTemplateObj.Content) : '';
      const body = raw || `Thanks for choosing {{practice_name}}, {{first_name}}. We'd love a quick review of your visit.`;
      return this._applySampleVars(body);
    },

    practiceName() {
      const site = (this.sites || []).find((s) => s.SiteID === this.siteID);
      if (site && site.Name) return site.Name;
      if (this.allSettings && this.allSettings.GENERAL_PRACTICE_NAME && this.allSettings.GENERAL_PRACTICE_NAME.PropertyValue) {
        return this.allSettings.GENERAL_PRACTICE_NAME.PropertyValue;
      }
      return 'Your practice';
    },
    practiceInitial() {
      return (this.practiceName || 'P').trim().charAt(0).toUpperCase();
    },
  },
};
</script>

<style lang="scss" scoped>
$primary: #0f766e;
$primary-subtle: #edfbf6;
$border: #e2e8f0;
$border-strong: #cbd5e1;
$neutral-500: #64748b;
$neutral-400: #94a3b8;
$neutral-900: #0f172a;

.rv-settings { display: block; }
.rv-loading { text-align: center; padding: 60px 0; }

.rv-settings-grid { display: grid; grid-template-columns: 1fr 372px; gap: 22px; align-items: start; }
.rv-settings-col { display: flex; flex-direction: column; gap: 18px; }
.rv-subsection { display: flex; flex-direction: column; gap: 18px; transition: opacity 0.2s ease; }
.rv-subsection.is-disabled { opacity: 0.5; pointer-events: none; }

.rv-card {
  background: #fff; border: 1px solid $border; border-radius: 14px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05); padding: 22px;
}
.rv-card-title { font-weight: 800; font-size: 15px; color: $neutral-900; }
.rv-card-sub { font-size: 13px; color: $neutral-500; margin-top: 2px; }
.rv-card-sub.mb-3 { margin-bottom: 16px; }
.rv-card-head-row { display: flex; align-items: flex-start; justify-content: space-between; gap: 14px; }

.rv-master { display: flex; align-items: center; gap: 16px; border-color: $border;
  &.is-on { border-color: #cbeee6; }
}
.rv-master-icon {
  width: 42px; height: 42px; border-radius: 11px; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  background: $primary-subtle; color: $primary; font-size: 18px;
}
.rv-master-text { flex: 1; min-width: 0; }

.rv-hint { font-size: 12px; color: $neutral-400; margin: 6px 0 0; }
.rv-hint.mt-2 { margin-top: 8px; }
.rv-field-label { font-size: 12px; font-weight: 700; color: #475569; display: block; margin-bottom: 6px; }

.rv-channel-toggles { display: flex; gap: 24px; margin-bottom: 18px; }
.rv-channel-toggle { display: flex; align-items: center; gap: 9px; cursor: pointer; margin: 0; }
.rv-channel-label { font-weight: 600; font-size: 13.5px; color: $neutral-900; }

.rv-template-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }

/* Switch (transparent input on top = click target) */
.rv-switch { position: relative; display: inline-block; width: 42px; height: 24px; cursor: pointer; margin: 0; flex-shrink: 0; }
.rv-switch input { position: absolute; inset: 0; opacity: 0; margin: 0; cursor: pointer; z-index: 2; }
.rv-switch-track { position: absolute; inset: 0; background: $border-strong; border-radius: 999px; transition: background 0.2s ease; }
.rv-switch-thumb { position: absolute; top: 3px; left: 3px; width: 18px; height: 18px; border-radius: 999px; background: #fff; box-shadow: 0 1px 2px rgba(0, 0, 0, 0.25); transition: transform 0.2s ease; }
.rv-switch input:checked ~ .rv-switch-track { background: $primary; }
.rv-switch input:checked ~ .rv-switch-track .rv-switch-thumb { transform: translateX(18px); }

/* Timing / follow-up inputs */
.rv-radio-row { display: flex; align-items: flex-start; gap: 11px; }
.rv-radio-row.mt-2 { margin-top: 14px; }
.rv-radio-row input[type="radio"] { margin-top: 4px; accent-color: $primary; }
.rv-radio-inline { display: flex; align-items: center; gap: 9px; flex-wrap: wrap; font-size: 13.5px; color: #334155; font-weight: 600; margin: 0; cursor: pointer; }
.rv-radio-stack { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
.rv-radio-desc { font-size: 12.5px; color: $neutral-500; margin-top: 2px; }

.rv-num-input {
  width: 64px; border: 1px solid $border-strong; border-radius: 9px; padding: 7px 9px;
  font-family: inherit; font-size: 14px; text-align: center; box-sizing: border-box;
  &:disabled { background: #f1f5f9; color: $neutral-400; }
}
.rv-select {
  appearance: none; -webkit-appearance: none;
  border: 1px solid $border-strong; border-radius: 9px; background: #fff;
  // height/line-height are LOAD-BEARING, not cosmetic. Conversations.vue ships an UNSCOPED
  // `select { height: 20px }` that leaks app-wide; with box-sizing:border-box and 14px of
  // vertical padding that leaves a ~4px content box, and `overflow: clip` then hides the
  // selected option entirely — the dropdown renders as an empty box with a chevron. 36px
  // matches .rv-num-input beside it. Remove these and the value disappears again.
  height: 36px; line-height: 1.4;
  padding: 7px 30px 7px 12px; font-size: 13.5px; color: #334155; font-family: inherit; cursor: pointer;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
  background-repeat: no-repeat; background-position: right 10px center;
  &:disabled { background-color: #f1f5f9; color: $neutral-400; }
}
.rv-unit { min-width: 110px; }
.rv-time { min-width: 120px; }

.rv-followup-config {
  display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
  margin-top: 18px; padding-top: 18px; border-top: 1px solid #f1f5f9;
  font-size: 13.5px; color: #334155;
}

// ── Procedure-code exclusion picker (ported from IV PrecheckSettingsModal .ivs-proc-select) ──
//
// ⛔ CONTAINMENT: every ::v-deep below is nested INSIDE .rv-proc-select on purpose. A top-level
// ::v-deep on .multiselect__* compiles to a selector with no scope attribute and would restyle
// every vue-multiselect in the app — including the SMS/email template pickers on THIS page.
// Keep new rules nested. (cypress/node-tests/reviews-settings-timing-exclusions.test.js pins it.)
.rv-proc-select {
  // Full card width, like IV's picker. Settings/Settings.scss ships an UNSCOPED
  // `.multiselect { max-width: 500px }` that caps every multiselect in the bundle; this scoped
  // root rule (.rv-proc-select[data-v-…]) outranks it here without touching any other page.
  max-width: none;

  ::v-deep .multiselect__tags {
    min-height: 38px;
    padding: 6px 40px 2px 8px;
    border: 1px solid #d7dedb;
    border-radius: 9px;
    font-size: 13px;
  }

  ::v-deep .multiselect--active .multiselect__tags {
    border-color: #0f7259;
    box-shadow: 0 0 0 3px rgba(15, 114, 89, 0.12);
  }

  ::v-deep .multiselect__input,
  ::v-deep .multiselect__placeholder,
  ::v-deep .multiselect__single {
    font-size: 13px;
    padding: 0;
    margin-bottom: 4px;
    background: transparent;
  }

  // The chips are our own .rv-chip markup via the tag slot, so strip the library's tag styling.
  ::v-deep .multiselect__tag { background: none; padding: 0; margin: 0 6px 4px 0; }

  ::v-deep .multiselect__option--highlight {
    background: #0f7259;
    color: #fff;
  }
  ::v-deep .multiselect__option--selected {
    background: #e9f6f1;
    color: #0b5c49;
    font-weight: 700;
  }
  ::v-deep .multiselect__option--selected.multiselect__option--highlight {
    background: #0b5c49;
    color: #fff;
  }

  // Long "D3310 - endodontic therapy, anterior tooth …" labels must not widen the control.
  ::v-deep .multiselect__option {
    font-size: 13px;
    min-height: 34px;
    padding: 9px 12px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  ::v-deep .multiselect__spinner:after,
  ::v-deep .multiselect__spinner:before { border-top-color: #0f7259; }
}

.rv-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 28px;
  padding: 0 6px 0 11px;
  background: #e9f6f1;
  color: #0b5c49;
  border: 1px solid #bfe4d8;
  border-radius: 999px;
  font-size: 12.5px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.01em;
}

.rv-chip-x {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border: none;
  background: transparent;
  color: #0b5c49;
  border-radius: 50%;
  font-size: 10px;
  cursor: pointer;
  opacity: 0.7;

  &:hover { background: #cdeadf; opacity: 1; }
}

.rv-proc-foot {
  display: flex; align-items: center; justify-content: space-between; gap: 10px; flex-wrap: wrap;
  margin-top: 8px;
  .rv-hint { margin: 0; }
}
.rv-hint-warn { color: #b45309; }

.rv-linkbtn {
  border: none;
  background: transparent;
  color: #0f7259;
  font-size: 12.5px;
  font-weight: 700;
  cursor: pointer;
  padding: 0 4px;

  &:hover { text-decoration: underline; }
}

/* Who gets asked (sendRules) */
.rv-rule-row { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.rv-rule-label { font-size: 13.5px; font-weight: 600; color: #334155; margin: 0; }
.rv-cap { min-width: 130px; }
.rv-rule-toggle { margin-top: 18px; padding-top: 18px; border-top: 1px solid #f1f5f9; }
.rv-rule-title { font-weight: 700; font-size: 13.5px; color: $neutral-900; }

/* Gate */
.rv-gate-options { display: flex; flex-direction: column; gap: 12px; }
.rv-gate-option {
  display: flex; gap: 11px; align-items: flex-start; cursor: pointer; margin: 0;
  border: 1px solid $border; border-radius: 11px; padding: 13px 15px; transition: border-color 0.15s ease, background 0.15s ease;
  input { margin-top: 2px; accent-color: $primary; }
  &.active { border-color: #9fd7cd; background: $primary-subtle; }
}
.rv-gate-title { font-weight: 700; font-size: 13.5px; color: $neutral-900; }
.rv-gate-desc { font-size: 12.5px; color: $neutral-500; margin-top: 2px; }

.rv-notify { margin-top: 18px; padding-top: 18px; border-top: 1px solid #f1f5f9; }
.rv-text-input {
  width: 100%; border: 1px solid $border-strong; border-radius: 9px; padding: 8px 11px;
  font-family: inherit; font-size: 13.5px; color: #334155; box-sizing: border-box;
}
.rv-mono { font-family: monospace; font-size: 12.5px; }

/* Missing-review-link warning (amber, matching .rv-unverified) */
.rv-link-warning {
  display: flex; align-items: flex-start; gap: 10px;
  margin-bottom: 14px; padding: 12px 14px;
  background: #fffbeb; border: 1px solid #fde68a; border-radius: 10px;
  font-size: 13px; line-height: 1.5; color: #78350f;
  .fa { color: #b45309; font-size: 15px; margin-top: 1px; }
  b { font-weight: 700; }
}

/* Review links */
.rv-link-list { display: flex; flex-direction: column; }
.rv-link-row { display: flex; align-items: flex-start; gap: 13px; padding: 16px 0; border-top: 1px solid #f1f5f9; }
.rv-link-mark { width: 34px; height: 34px; border-radius: 9px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; font-weight: 800; font-size: 15px; margin-top: 2px; }
.rv-link-body { flex: 1; min-width: 0; }
.rv-link-name-row { display: flex; align-items: center; gap: 9px; margin-bottom: 8px; }
.rv-link-name { font-weight: 700; font-size: 14px; color: $neutral-900; }
.rv-verified { display: inline-flex; align-items: center; gap: 4px; font-size: 11px; font-weight: 600; color: #0d9488; background: #ecfdf5; padding: 2px 8px; border-radius: 999px; }
.rv-unverified { display: inline-flex; align-items: center; gap: 4px; font-size: 11px; font-weight: 600; color: #b45309; background: #fffbeb; padding: 2px 8px; border-radius: 999px; }
.rv-link-input-row { display: flex; gap: 8px; }
.rv-link-switch { margin-top: 6px; }

/* Google Business Profile connect block (Phase B1) */
.rv-gc { margin-bottom: 10px; }
.rv-gc-connected { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
.rv-gc-status {
  display: inline-flex; align-items: center; gap: 6px;
  font-size: 13px; color: #0d9488; font-weight: 600;
  .fa { color: #0d9488; }
  b { color: $neutral-900; font-weight: 700; }
}
.rv-gc-needs-label { font-size: 12.5px; color: $neutral-500; margin-bottom: 7px; }
.rv-gc-needs-row { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
.rv-gc-select { max-width: 340px; ::v-deep .custom-select { border-radius: 9px; } }
.rv-gc-btn { white-space: nowrap; .fa { margin-right: 4px; } }
.rv-gc-error {
  display: inline-flex; align-items: center; gap: 5px;
  font-size: 12.5px; color: #b45309; margin-left: 10px;
  .fa { color: #b45309; }
}
.rv-gc-pending {
  display: flex; align-items: flex-start; gap: 8px;
  padding: 10px 12px; border-radius: 9px;
  background: #f8fafc; border: 1px solid $border;
  font-size: 12.5px; line-height: 1.5; color: $neutral-500;
  b { color: $neutral-900; font-weight: 700; }
  .fa { color: $neutral-400; margin-top: 2px; }
}

/* Buttons */
.rv-btn { border: none; cursor: pointer; font-family: inherit; font-size: 13px; font-weight: 600; padding: 9px 16px; border-radius: 9px; transition: background 0.15s ease;
  &:disabled { opacity: 0.6; cursor: not-allowed; }
}
.rv-btn-secondary { background: #fff; border: 1px solid $border-strong; color: #334155; white-space: nowrap; &:not(:disabled):hover { background: #f8fafc; } }
.rv-btn-primary { background: $primary; color: #fff; &:not(:disabled):hover { background: #0b5f58; } }

.rv-save-bar { display: flex; justify-content: flex-end; padding-top: 4px; }

/* Preview rail */
.rv-preview-rail { position: sticky; top: 130px; display: flex; flex-direction: column; gap: 14px; }
.rv-preview-card { padding: 18px; }
.rv-preview-head { display: flex; align-items: center; gap: 7px; font-size: 12px; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase; color: $neutral-500; margin-bottom: 14px; }
.rv-preview-block { margin-bottom: 16px; &:last-child { margin-bottom: 0; } }
.rv-preview-label { font-size: 11px; font-weight: 700; color: $neutral-400; margin-bottom: 7px; }
.rv-sms-bubble { background: #f1f5f9; border-radius: 16px 16px 16px 4px; padding: 11px 14px; font-size: 13.5px; line-height: 1.5; color: #334155; max-width: 280px; white-space: pre-wrap; word-break: break-word; }
.rv-preview-note { font-size: 11px; color: $neutral-400; margin-top: 6px; text-align: right; }

.rv-email-card { border: 1px solid $border; border-radius: 12px; overflow: hidden; }
.rv-email-header { background: #0c0f0e; padding: 10px 14px; display: flex; align-items: center; gap: 8px; color: #fff; font-size: 12px; font-weight: 600; }
.rv-email-logo { width: 18px; height: 18px; border-radius: 5px; background: $primary; color: #fff; font-size: 10px; font-weight: 800; display: flex; align-items: center; justify-content: center; }
.rv-email-body { padding: 14px; }
.rv-email-title { font-weight: 800; font-size: 15px; margin-bottom: 6px; color: $neutral-900; }
.rv-email-text { font-size: 12.5px; color: $neutral-500; line-height: 1.5; margin-bottom: 12px; }
.rv-email-cta { display: inline-block; background: $primary; color: #fff; font-size: 12px; font-weight: 700; padding: 8px 16px; border-radius: 8px; }
.rv-preview-empty { font-size: 12.5px; color: $neutral-400; padding: 8px 0; }

.rv-preview-tip { background: $primary-subtle; border-radius: 14px; padding: 16px 18px; font-size: 12.5px; color: #475569; line-height: 1.5; display: flex; gap: 10px;
  .fa { color: $primary; margin-top: 1px; }
}

@media (max-width: 1100px) {
  .rv-settings-grid { grid-template-columns: 1fr; }
  .rv-preview-rail { position: static; }
  .rv-template-grid { grid-template-columns: 1fr; }
}
</style>
